import { searchApi } from '@api/search.api';
import { FullSearchResponseData, SearchContentSection, SearchResponseData } from '@api/types';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';

// INITIAL STATE
export type SearchState = {
  isInFullSearch?: boolean;
  query?: string;
  section: SearchContentSection;
  results: FullSearchResponseData;
  offset: { [key in SearchContentSection]?: number };
  totalCountInSections: { [key in SearchContentSection]?: number };
  productsCategoryLink?: string;
  fullSearchProductTotalCount?: number;
};

const initialState: SearchState = {
  isInFullSearch: false,
  query: '',
  section: 'products',
  offset: {},
  results: {},
  totalCountInSections: {},
  productsCategoryLink: '',
  fullSearchProductTotalCount: 0,
};

// REDUCERS
export const searchSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    // Set full search mode
    setFullSearch: (state, action: PayloadAction<boolean>) => {
      state.isInFullSearch = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setSearchSection: (state, action: PayloadAction<SearchContentSection>) => {
      state.section = action.payload;
    },
    setSearchOffset: (state, action: PayloadAction<number>) => {
      const activeSection = state.section;
      state.offset[activeSection] = action.payload;
    },
    resetSearchOffsets: (state) => {
      state.offset = {};
    },
    setProductsCategoryLink: (state, action: PayloadAction<string | undefined | null>) => {
      state.productsCategoryLink = action.payload || '';
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(searchApi.endpoints.fullSearch.matchFulfilled, (state, action) => {
      const data = action.payload.data;
      state.results = data || {};
      state.totalCountInSections.articles = data?.articles?.totalCount;
      state.totalCountInSections.documents = data?.documents?.totalCount;
      state.totalCountInSections.products = data?.products?.totalCount;
      state.totalCountInSections.accessories = data?.accessories?.totalCount;
    });
    builder.addMatcher(searchApi.endpoints.searchArticles.matchFulfilled, (state, action) => {
      state.results.articles = action.payload.data;
    });
    builder.addMatcher(searchApi.endpoints.searchDocuments.matchFulfilled, (state, action) => {
      state.results.documents = action.payload.data;
    });
    builder.addMatcher(searchApi.endpoints.searchProducts.matchFulfilled, (state, action) => {
      state.results.products = action.payload.data;
    });
    builder.addMatcher(searchApi.endpoints.searchAccessories.matchFulfilled, (state, action) => {
      state.results.accessories = action.payload.data;
    });
    builder.addMatcher(searchApi.endpoints.fullSearch.matchFulfilled, (state, action) => {
      state.fullSearchProductTotalCount = action.payload.data?.products?.totalCount;
    });
  },
});

// ACTIONS
export const {
  setFullSearch,
  setSearchSection,
  setSearchQuery,
  setSearchOffset,
  setProductsCategoryLink,
  resetSearchOffsets,
} = searchSlice.actions;

// SELECTORS
export const getSearchSection = createSelector(
  (state: RootState) => state.search.section,
  (type) => type
);

export const getSearchQuery = createSelector(
  (state: RootState) => state.search.query || '',
  (query) => query
);

export const getSearchResults = createSelector(
  (state: RootState) => state.search.results || {},
  (results) => results
);

export const getSearchResultsInCurrentSection = createSelector(
  getSearchResults,
  getSearchSection,
  (results, section) => results[section] || ({ items: [], totalCount: 0 } as SearchResponseData)
);

export const getTotalCountInSections = createSelector(
  (state: RootState) => state.search.totalCountInSections,
  (count) => count
);

export const getVisibleItemCountInCurrentSection = createSelector(
  getSearchResultsInCurrentSection,
  (sectionResults) => sectionResults.totalCount || 0
);

export const getSearchResultsCountInCurrentSection = createSelector(
  getTotalCountInSections,
  getSearchSection,
  (results, currentSection) => results?.[currentSection] || 0
);

export const isInFullSearch = createSelector(
  (state: RootState) => state.search.isInFullSearch || false,
  (flag) => flag
);

export const getTotalSearchHits = createSelector(
  (state: RootState) => state.search.totalCountInSections || {},
  (results) => {
    // Sum all counts of search results except of accessories
    return Object.entries(results)
      .filter(([key]) => key !== 'accessories')
      .map(([_, result]) => result)
      .reduce((accumulator = 0, currentValue = 0) => accumulator + currentValue, 0);
  }
);

export const getSearchOffsets = createSelector(
  (state: RootState) => state.search.offset,
  (offset) => offset
);

export const getSearchOffset = createSelector(
  getSearchOffsets,
  getSearchSection,
  (offsets, currentSection) => offsets[currentSection] || 0
);

// Category Links
export const getProductsCategoryLink = createSelector(
  (state: RootState) => state.search.productsCategoryLink,
  (link) => link
);

export const getFullSearchProductTotalCount = createSelector(
  (state: RootState) => state.search.fullSearchProductTotalCount || 0,
  (count) => count
);

export default searchSlice.reducer;
